import { ChangeEvent, FunctionComponent, useEffect, useState } from 'react';
import { Transaction } from '../../core/domain/module';
import { TransactionFieldProps } from './TransactionField';
import ValidationMessage from '../ValidationMessage/ValidationMessage';
import { isSpouseProduct } from '../../utilities/isSpouseProduct';

import styles from './BillingFrequency.module.scss';

export const BillingFrequency: FunctionComponent<TransactionFieldProps> = ({ field, className, showValidation, changeHandler, defaultValue, useEffectFunc = useEffect }) => {
 	const transaction = Transaction.Instance();
	const [annualPrice, setAnnualPrice] = useState<number>(0);
	const [monthlyPrice, setMonthlyPrice] = useState<number>(0);
	const [currentFrequency, setCurrentFrequency] = useState<string | undefined>();

	useEffectFunc(() => {
		const transactionUpdateSub = transaction.Updates.Subscribe(() => {
			const productData = transaction.Data.productData;
			const annualProduct = productData?.find(
				(p) => p.attributes.paymentFrequency === 'annual' && !isSpouseProduct(p)
			);
			const monthlyProduct = productData?.find(
				(p) => p.attributes.paymentFrequency === 'monthly' && !isSpouseProduct(p)
			);
			setAnnualPrice(annualProduct?.attributes.price || 0);
			setMonthlyPrice(monthlyProduct?.attributes.price || 0);
			setCurrentFrequency(defaultValue);
		});

		return () => {
			transaction.Updates.Cancel(transactionUpdateSub);
		};
	}, []);

	useEffectFunc(() => {
		if (currentFrequency) {
			field.SetValue(currentFrequency);
		}
	}, [currentFrequency]);

	const onFrequencyChange = (e: ChangeEvent<HTMLElement>) => {
		const input = e.target as HTMLInputElement;
		if (input.checked) {
			field.SetValue(input.value);
			if (transaction.Data.purchaseProducts?.primary) {
				transaction.Data.purchaseProducts.primary = transaction.Data.productData?.find(
					(p) => p.attributes.paymentFrequency === input.value && !isSpouseProduct(p)
				);
			}
			if (transaction.Data.purchaseProducts?.spouse) {
				transaction.Data.purchaseProducts.spouse = transaction.Data.productData?.find(
					(p) => p.attributes.paymentFrequency === input.value && isSpouseProduct(p)
				);
			}
			transaction.Updates.Publish(true);
		}
		changeHandler?.(e);
	};

	if (!annualPrice || !monthlyPrice) {
		return null;
	}

	return (
		<>
			<div className={styles.radioButtonWrapper}>
				<input
					type="radio"
					id="annual"
					name="billingFrequency"
					value="annual"
					className={`${styles.radioButton} ${className}`}
					defaultChecked={currentFrequency === 'annual'}
					onChange={onFrequencyChange}
					data-testid="billingFrequencyAnnual"
				/>
				<label htmlFor="annual" className={styles.label}>
					<span className={styles.annual}>
						${annualPrice.toFixed(2)}/year <em>(Save ${(monthlyPrice * 12 - annualPrice).toFixed(2)}!)</em>
					</span>
				</label>
			</div>

			<div className={styles.radioButtonWrapper}>
				<input
					type="radio"
					id="monthly"
					name="billingFrequency"
					value="monthly"
					className={`${styles.radioButton} ${className}`}
					defaultChecked={currentFrequency === 'monthly'}
					onChange={onFrequencyChange}
					data-testid="billingFrequencyMonthly"
				/>
				<label htmlFor="monthly" className={styles.label}>
					<span className={styles.monthly}>${monthlyPrice?.toFixed(2)}/month</span>
				</label>
			</div>
			{showValidation && <ValidationMessage>{field.ValidationMessage}</ValidationMessage>}
		</>
	);
};
